import { useState } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import { BiLock } from "react-icons/bi";
import { fill } from "utils/helpers/template";
import IntegrationKeysDialog from "./IntegrationKeysDialog";
import { INTEGRATION_CATEGORIES, integrationOf } from "./onboarding";

const keysBadge = ({ connector, keys, filled }, labels) => {
  const vendor = get(connector, "vendor") || get(connector, "label", "");
  if (isEmpty(keys))
    return { done: true, text: fill(labels.NO_KEYS, { vendor }) };
  if (filled === keys.length)
    return { done: true, text: fill(labels.CONFIGURED, { vendor }) };
  return {
    done: false,
    text: fill(labels.KEYS_STATUS, { vendor, filled, total: keys.length }),
  };
};

const configurable = ({ providers, keys }) =>
  providers.length > 1 || keys.length > 0;

const IntegrationsStep = ({
  catalog,
  labels,
  keysLocked,
  lockedLabels,
  onConfigureKey,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const [configuring, setConfiguring] = useState(null);

  const save = (integration, patch) =>
    setFieldValue(`integrations.${integration.category}`, {
      ...integration.state,
      connector: get(integration, "connector.key"),
      ...patch,
    });

  const open = configuring ? integrationOf(values, catalog, configuring) : null;

  return (
    <>
      {keysLocked ? (
        <div className="onboarding__notice onboarding__notice--info onboarding__notice--action">
          <BiLock size={20} aria-hidden="true" />
          <span>{lockedLabels.LOCKED}</span>
          <button
            type="button"
            className="onboarding__button onboarding__button--primary"
            onClick={onConfigureKey}
          >
            {lockedLabels.CONFIGURE}
          </button>
        </div>
      ) : null}
      {INTEGRATION_CATEGORIES.map((category) => {
        const integration = integrationOf(values, catalog, category);
        const text = get(labels.ITEMS, category, {});
        const badge = keysBadge(integration, labels);
        return (
          <div
            key={category}
            className={classNames("onboarding__card", {
              "onboarding__card--on": integration.on,
            })}
          >
            <button
              type="button"
              aria-pressed={integration.on}
              className="onboarding__toggle"
              disabled={!integration.connector}
              onClick={() => save(integration, { on: !integration.on })}
            >
              <span className="onboarding__switch" aria-hidden="true" />
              <span className="onboarding__toggle-text">
                <strong>{text.TITLE}</strong>
                <span>{text.DESCRIPTION}</span>
              </span>
            </button>
            {integration.on ? (
              <div className="onboarding__card-actions">
                <span
                  className={classNames("onboarding__badge", {
                    "onboarding__badge--done": badge.done,
                    "onboarding__badge--todo": !badge.done,
                  })}
                >
                  {badge.text}
                </span>
                {configurable(integration) ? (
                  <button
                    type="button"
                    className="onboarding__button onboarding__button--outline"
                    onClick={() => setConfiguring(category)}
                  >
                    {labels.CONFIGURE}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      {open ? (
        <IntegrationKeysDialog
          title={get(labels.ITEMS, [open.category, "TITLE"], open.category)}
          vendor={
            get(open, "connector.vendor") || get(open, "connector.label", "")
          }
          providers={open.providers}
          provider={get(open, "connector.key", "")}
          keys={open.keys}
          entered={open.entered}
          present={open.present}
          locked={keysLocked}
          labels={{ ...labels.DIALOG, PROVIDER: labels.PROVIDER }}
          onProvider={(connector) => save(open, { connector })}
          onChange={(entered) =>
            save(open, {
              values: {
                ...get(open, "state.values", {}),
                [open.connector.key]: entered,
              },
            })
          }
          onClose={() => setConfiguring(null)}
        />
      ) : null}
    </>
  );
};

IntegrationsStep.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object).isRequired,
  labels: PropTypes.object.isRequired,
  keysLocked: PropTypes.bool,
  lockedLabels: PropTypes.shape({
    LOCKED: PropTypes.string,
    CONFIGURE: PropTypes.string,
  }),
  onConfigureKey: PropTypes.func,
};

IntegrationsStep.defaultProps = {
  keysLocked: false,
  lockedLabels: {},
  onConfigureKey: null,
};

export default IntegrationsStep;
